/**
 * Product Detail Page JavaScript
 * Handles variant selection, add-to-cart, gallery, and all PDP interactions
 * Converted from Preact components: AddToCart.tsx, mobile-add-to-cart.tsx, ProductImageSlider
 */

// ============================================
// Variant Selector Web Component
// ============================================

class VariantSelector extends HTMLElement {
  constructor() {
    super();
    const variantJson = this.querySelector('[data-variant-json]');
    if (!variantJson) {
      console.warn('Variant data missing for selector', this);
      return;
    }

    this.variantData = JSON.parse(variantJson.textContent);
    this.addEventListener('change', this.onVariantChange);

    // Initialize selections and availability on load
    this.updateOptions();
    this.updateMasterId();
    if (this.currentVariant) {
      this.renderProductInfo();
      this.updateOptionAvailability();
    }
  }

  onVariantChange(event) {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
      this.updateOptionAvailability();
    }
  }

  updateOptions() {
    const selected = Array.from(this.querySelectorAll('select, input[type="radio"]:checked'));
    const optionValues = [];
    selected.forEach((el) => {
      const idx = parseInt(el.dataset.optionIndex, 10);
      if (Number.isInteger(idx)) {
        optionValues[idx] = el.value;
      } else {
        optionValues.push(el.value);
      }
    });
    this.options = optionValues;
  }

  updateMasterId() {
    this.currentVariant = this.variantData.find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;

    const mediaGallery = document.querySelector('[data-product-gallery]');
    if (!mediaGallery) return;

    const newMedia = mediaGallery.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    if (!newMedia) return;

    const activeMedia = mediaGallery.querySelector('.product-gallery__media--active');
    if (activeMedia) {
      activeMedia.classList.remove('product-gallery__media--active');
    }
    newMedia.classList.add('product-gallery__media--active');

    // Update thumbnails
    const activeThumbnail = mediaGallery.querySelector('.product-gallery__thumbnail--active');
    if (activeThumbnail) {
      activeThumbnail.classList.remove('product-gallery__thumbnail--active');
    }
    const newThumbnail = mediaGallery.querySelector(`[data-thumbnail][data-media-id="${this.currentVariant.featured_media.id}"]`);
    if (newThumbnail) {
      newThumbnail.classList.add('product-gallery__thumbnail--active');
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById('Share-' + this.dataset.section);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`[data-product-form="${this.dataset.section}"]`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(`price-${this.dataset.section}`);

        if (source && destination) destination.innerHTML = source.innerHTML;

        if (destination) destination.classList.remove('visibility-hidden');
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);

        // Update Sticky Bar Price
        const stickyPrice = document.querySelector(`[data-mobile-price]`);
        if (stickyPrice && this.currentVariant) {
          const newPrice = new Intl.NumberFormat(document.documentElement.lang, {
            style: 'currency',
            currency: window.Shopify ? window.Shopify.currency.active : 'USD'
          }).format(this.currentVariant.price / 100);
          stickyPrice.textContent = newPrice;
        }

        // Update Sticky Bar Button State
        const stickyBar = document.querySelector(`[data-sticky-bar]`);
        if (stickyBar) {
          const stickyBtn = stickyBar.querySelector('[data-mobile-add-to-cart]');
          if (stickyBtn) {
            if (!this.currentVariant.available) {
              stickyBtn.disabled = true;
              stickyBtn.textContent = window.variantStrings.soldOut;
            } else {
              stickyBtn.disabled = false;
              stickyBtn.textContent = window.variantStrings.addToCart;
            }
          }
        }
      });
  }

  updateOptionAvailability() {
    if (!this.variantData || !this.options) return;

    const optionInputs = this.querySelectorAll('[data-option-value]');
    optionInputs.forEach((input) => {
      const optionIndex = parseInt(input.dataset.optionIndex, 10);
      const testOptions = [...this.options];
      testOptions[optionIndex] = input.value;
      const matching = this.variantData.find((variant) => {
        return variant.options.every((opt, idx) => {
          return !testOptions[idx] || opt === testOptions[idx];
        });
      });
      input.disabled = !matching || !matching.available;
    });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.querySelector(`[data-product-form="${this.dataset.section}"]`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[data-add-to-cart-text]');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const productForm = document.querySelector(`[data-product-form="${this.dataset.section}"]`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[data-add-to-cart-text]');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }
}

customElements.define('variant-selector', VariantSelector);

// ============================================
// Product Gallery
// ============================================

class ProductGallery {
  constructor(element) {
    this.gallery = element;
    this.slides = this.gallery.querySelectorAll('[data-media-index]');
    this.thumbnails = this.gallery.querySelectorAll('[data-thumbnail]');
    this.prevButton = this.gallery.querySelector('[data-gallery-prev]');
    this.nextButton = this.gallery.querySelector('[data-gallery-next]');
    this.currentSlide = 0;
    this.lightbox = document.querySelector('[data-lightbox]');
    this.lightboxImage = this.lightbox?.querySelector('[data-lightbox-image]');
    this.lightboxPrev = this.lightbox?.querySelector('[data-lightbox-prev]');
    this.lightboxNext = this.lightbox?.querySelector('[data-lightbox-next]');
    this.lightboxOverlayClose = this.lightbox?.querySelectorAll('[data-lightbox-close]') || [];

    this.init();
  }

  init() {
    // Thumbnail clicks
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.goToSlide(index));
    });

    // Arrow navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.previousSlide());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }

    // Lightbox
    const lightboxTriggers = this.gallery.querySelectorAll('[data-lightbox-trigger]');
    lightboxTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        const slideEl = e.currentTarget.closest('[data-media-index]');
        const index = slideEl ? parseInt(slideEl.dataset.mediaIndex, 10) : this.currentSlide;
        this.openLightbox(index);
      });
    });

    // Lightbox controls
    if (this.lightbox) {
      this.lightboxOverlayClose.forEach(btn => btn.addEventListener('click', () => this.closeLightbox()));
      this.lightboxPrev?.addEventListener('click', () => this.lightboxStep(-1));
      this.lightboxNext?.addEventListener('click', () => this.lightboxStep(1));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.lightbox.hasAttribute('hidden')) this.closeLightbox();
        if (e.key === 'ArrowLeft' && !this.lightbox.hasAttribute('hidden')) this.lightboxStep(-1);
        if (e.key === 'ArrowRight' && !this.lightbox.hasAttribute('hidden')) this.lightboxStep(1);
      });
    }

    this.updateCounter();
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    // Hide all slides
    this.slides.forEach(slide => {
      slide.classList.remove('product-gallery__media--active');
    });

    // Show target slide
    this.slides[index].classList.add('product-gallery__media--active');

    // Update thumbnails
    this.thumbnails.forEach(thumb => {
      thumb.classList.remove('product-gallery__thumbnail--active');
    });
    if (this.thumbnails[index]) {
      this.thumbnails[index].classList.add('product-gallery__thumbnail--active');
    }

    this.currentSlide = index;
    this.updateCounter();
  }

  nextSlide() {
    const next = (this.currentSlide + 1) % this.slides.length;
    this.goToSlide(next);
  }

  previousSlide() {
    const prev = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prev);
  }

  updateCounter() {
    const currentEl = document.querySelector('[data-gallery-current]');
    if (currentEl) {
      currentEl.textContent = this.currentSlide + 1;
    }
  }

  openLightbox(index = 0) {
    if (!this.lightbox || !this.lightboxImage) return;
    if (!this.slides || this.slides.length === 0) return;

    // Ensure index is valid
    const safeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.currentSlide = safeIndex;

    const slide = this.slides[safeIndex];
    if (!slide) return;

    const triggerEl = slide.querySelector('[data-lightbox-trigger]');
    const img = slide.querySelector('img');
    const src = triggerEl?.dataset?.lightboxSrc || img?.src;

    if (!src) return;

    this.lightboxImage.src = src;
    this.lightboxImage.alt = img?.alt || '';
    this.lightbox.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    if (!this.lightbox) return;
    this.lightbox.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }

  lightboxStep(direction) {
    if (!this.slides || this.slides.length === 0) return;
    const nextIndex = (this.currentSlide + direction + this.slides.length) % this.slides.length;
    this.goToSlide(nextIndex);
    this.openLightbox(nextIndex);
  }
}

// Initialize galleries
document.addEventListener('DOMContentLoaded', () => {
  const galleries = document.querySelectorAll('[data-product-gallery]');
  galleries.forEach(gallery => new ProductGallery(gallery));
});

// ============================================
// Quantity Controls
// ============================================

class QuantityControls {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-quantity-minus]')) {
        this.decreaseQuantity(e.target);
      }
      if (e.target.matches('[data-quantity-plus]')) {
        this.increaseQuantity(e.target);
      }
    });

    // Prevent invalid input
    document.addEventListener('input', (e) => {
      if (e.target.matches('[data-quantity-input]')) {
        this.validateQuantity(e.target);
      }
    });
  }

  decreaseQuantity(button) {
    const input = button.closest('.quantity-selector').querySelector('[data-quantity-input]');
    const currentValue = parseInt(input.value, 10);
    const newValue = Math.max(1, currentValue - 1);
    input.value = newValue;
  }

  increaseQuantity(button) {
    const input = button.closest('.quantity-selector').querySelector('[data-quantity-input]');
    const currentValue = parseInt(input.value, 10);
    const newValue = currentValue + 1;
    input.value = newValue;
  }

  validateQuantity(input) {
    const value = parseInt(input.value, 10);
    if (isNaN(value) || value < 1) {
      input.value = 1;
    }
  }
}

new QuantityControls();

// ============================================
// Mobile Sticky Bar
// ============================================

class MobileStickyBar {
  constructor() {
    this.bar = document.querySelector('[data-sticky-bar]');
    if (!this.bar) return;
    this.sectionId = this.bar.dataset.sectionId;

    this.init();
  }

  init() {
    // Show/hide based on scroll
    let lastScroll = 0;
    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 300) {
        if (currentScroll > lastScroll) {
          this.bar.classList.add('sticky-mobile-bar--visible');
        } else {
          this.bar.classList.remove('sticky-mobile-bar--visible');
        }
      } else {
        this.bar.classList.remove('sticky-mobile-bar--visible');
      }

      lastScroll = currentScroll;
    }, { passive: true });

    // Handle mobile add to cart click
    const mobileAddBtn = this.bar.querySelector('[data-mobile-add-to-cart]');
    if (mobileAddBtn) {
      mobileAddBtn.addEventListener('click', () => {
        const mainForm = this.sectionId
          ? document.querySelector(`[data-product-form="${this.sectionId}"]`)
          : document.querySelector('[data-product-form]');
        if (mainForm) {
          mainForm.querySelector('[name="add"]').click();
        }
      });
    }
  }
}

new MobileStickyBar();

// ============================================
// Add to Cart Form Handler
// ============================================

document.addEventListener('DOMContentLoaded', () => {
  const productForms = document.querySelectorAll('form[action*="/cart/add"]');

  productForms.forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const submitButton = form.querySelector('[data-add-to-cart]');
      const buttonText = submitButton.querySelector('[data-add-to-cart-text]');
      const originalText = buttonText.textContent;

      // Show loading state
      submitButton.disabled = true;
      submitButton.classList.add('is-loading');

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const item = await response.json();

        // Success feedback - button state
        submitButton.classList.remove('is-loading');
        submitButton.classList.add('is-success');

        // Show toast notification
        showAddToCartToast(item.product_title || item.title);

        setTimeout(() => {
          submitButton.classList.remove('is-success');
          submitButton.disabled = false;
        }, 2000);

        // Dispatch event for cart drawer
        document.dispatchEvent(new CustomEvent('cart:add', { detail: item }));

        // Update cart count
        const cart = await window.StuffedStore.getCart();
        const countElements = document.querySelectorAll('[data-cart-count]');
        countElements.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'block' : 'none';
        });

      } catch (error) {
        console.error('Add to cart error:', error);
        submitButton.classList.remove('is-loading');
        buttonText.textContent = 'Error - Try again';
        setTimeout(() => {
          buttonText.textContent = originalText;
          submitButton.disabled = false;
        }, 2000);
      }
    });
  });
});

/**
 * Show a toast notification when item is added to cart
 */
function showAddToCartToast(productTitle) {
  // Remove any existing toast
  const existingToast = document.querySelector('.toast');
  if (existingToast) {
    existingToast.remove();
  }

  // Create toast element
  const toast = document.createElement('div');
  toast.className = 'toast toast--success';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.innerHTML = `
    <svg class="toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke-width="2" stroke-linecap="round"/>
      <polyline points="22 4 12 14.01 9 11.01" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    <span>Added to cart</span>
  `;

  document.body.appendChild(toast);

  // Auto-remove after delay
  setTimeout(() => {
    toast.classList.add('is-leaving');
    setTimeout(() => toast.remove(), 200);
  }, 3000);
}

// Set up variant strings for translation
window.variantStrings = {
  addToCart: document.querySelector('[data-add-to-cart-text]')?.textContent || 'Add to cart',
  soldOut: 'Sold out',
  unavailable: 'Unavailable'
};

// Gift toggle
(function () {
  const giftToggle = document.querySelector('[data-gift-toggle]');
  const giftMessage = document.querySelector('[data-gift-message]');
  if (!giftToggle || !giftMessage) return;

  const syncGiftState = () => {
    const enabled = giftToggle.checked;
    giftMessage.disabled = !enabled;
    if (!enabled) {
      giftMessage.value = '';
    }
  };

  giftToggle.addEventListener('change', syncGiftState);
  syncGiftState();
})();

// ============================================
// Hover Zoom (Desktop)
// ============================================

class HoverZoom {
  constructor() {
    this.containers = document.querySelectorAll('[data-hover-zoom]');
    if (!this.containers.length) return;

    // Only enable on desktop (non-touch devices)
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;

    this.init();
  }

  init() {
    this.containers.forEach(container => {
      const img = container.querySelector('img');
      const zoomSrc = container.dataset.zoomSrc;
      if (!img || !zoomSrc) return;

      // Create zoom lens element
      const lens = document.createElement('div');
      lens.className = 'hover-zoom-lens';
      container.appendChild(lens);

      // Preload high-res image
      const zoomImg = new Image();
      zoomImg.src = zoomSrc;

      container.addEventListener('mouseenter', () => {
        lens.style.backgroundImage = `url(${zoomSrc})`;
        lens.classList.add('is-active');
      });

      container.addEventListener('mouseleave', () => {
        lens.classList.remove('is-active');
      });

      container.addEventListener('mousemove', (e) => {
        const rect = container.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        lens.style.backgroundPosition = `${x}% ${y}%`;
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new HoverZoom());

// ============================================
// Pinch-to-Zoom (Mobile Lightbox)
// ============================================

class PinchZoom {
  constructor() {
    this.container = document.querySelector('[data-pinch-zoom-container]');
    this.image = this.container?.querySelector('img');
    if (!this.container || !this.image) return;

    this.scale = 1;
    this.lastScale = 1;
    this.startDistance = 0;
    this.translateX = 0;
    this.translateY = 0;
    this.lastTranslateX = 0;
    this.lastTranslateY = 0;
    this.isPinching = false;
    this.lastTap = 0;

    this.init();
  }

  init() {
    // Prevent default touch behaviors
    this.container.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false });
    this.container.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
    this.container.addEventListener('touchend', (e) => this.onTouchEnd(e));

    // Double-tap to zoom
    this.container.addEventListener('click', (e) => this.onDoubleTap(e));

    // Reset on lightbox close
    const lightbox = this.container.closest('[data-lightbox]');
    if (lightbox) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === 'hidden' && lightbox.hasAttribute('hidden')) {
            this.resetZoom();
          }
        });
      });
      observer.observe(lightbox, { attributes: true });
    }
  }

  getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  onTouchStart(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      this.isPinching = true;
      this.startDistance = this.getDistance(e.touches[0], e.touches[1]);
      this.lastScale = this.scale;
    } else if (e.touches.length === 1 && this.scale > 1) {
      this.lastTranslateX = this.translateX;
      this.lastTranslateY = this.translateY;
      this.startX = e.touches[0].clientX;
      this.startY = e.touches[0].clientY;
    }
  }

  onTouchMove(e) {
    if (e.touches.length === 2 && this.isPinching) {
      e.preventDefault();
      const distance = this.getDistance(e.touches[0], e.touches[1]);
      this.scale = Math.min(Math.max(this.lastScale * (distance / this.startDistance), 1), 4);
      this.applyTransform();
    } else if (e.touches.length === 1 && this.scale > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - this.startX;
      const dy = e.touches[0].clientY - this.startY;
      this.translateX = this.lastTranslateX + dx;
      this.translateY = this.lastTranslateY + dy;
      this.applyTransform();
    }
  }

  onTouchEnd(e) {
    this.isPinching = false;
    if (this.scale <= 1) {
      this.resetZoom();
    }
  }

  onDoubleTap(e) {
    const now = Date.now();
    if (now - this.lastTap < 300) {
      e.preventDefault();
      if (this.scale > 1) {
        this.resetZoom();
      } else {
        this.scale = 2.5;
        // Zoom toward tap point
        const rect = this.container.getBoundingClientRect();
        const tapX = (e.clientX - rect.left) / rect.width;
        const tapY = (e.clientY - rect.top) / rect.height;
        this.translateX = (0.5 - tapX) * rect.width * (this.scale - 1);
        this.translateY = (0.5 - tapY) * rect.height * (this.scale - 1);
        this.applyTransform();
      }
    }
    this.lastTap = now;
  }

  applyTransform() {
    this.image.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }

  resetZoom() {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.image.style.transform = '';
  }
}

document.addEventListener('DOMContentLoaded', () => new PinchZoom());
